import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Box, IconButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';
import useThemeStyles from '../../../hooks/useThemeStyles';
import urls from '../../../urls';
import { getProjectTypeConfig } from '../../../utils/projectTypeConfig';
import formatLastEdited from '../../../utils/dashboard/formatLastEdited';

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const { colors, isDark } = useThemeStyles();
  const [isHovered, setIsHovered] = useState(false);

  // Generate consistent random values based on project ID for paper stack effect
  const getStackTransform = (layerIndex) => {
    // Use project ID as seed for consistent randomness
    const hash = project._id
      ? project._id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : 0;
    const random = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    const baseSeed = hash + layerIndex * 100;
    const rotation = (random(baseSeed) - 0.5) * 8; // -4 to 4 degrees
    const translateX = (random(baseSeed + 1) - 0.5) * 16; // -8 to 8 pixels
    const translateY = (random(baseSeed + 2) - 0.5) * 12; // -6 to 6 pixels

    return {
      rotation,
      translateX,
      translateY,
    };
  };

  // Get project title
  const projectTitle = project.title || 'Untitled Project';
  const typeConfig = getProjectTypeConfig(project.type);
  const TypeIcon = typeConfig.icon;

  const lastEdited = formatLastEdited(project.updatedAt);
  const wordCount = project.wordCount || 0;
  const status = project.status || 'draft';
  const sharedRole = project.myRole && project.myRole !== 'owner' ? project.myRole : null;
  const goalWordCount =
    typeof project.goalWordCount === 'number' && project.goalWordCount > 0
      ? project.goalWordCount
      : null;
  const goalProgress = goalWordCount ? Math.min(1, wordCount / goalWordCount) : null;

  const statusLabel =
    status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1);

  // Build metadata text with both word count and last edited
  const metadataParts = [];
  if (wordCount > 0) {
    metadataParts.push(`${wordCount.toLocaleString()} words`);
  }
  if (lastEdited) {
    metadataParts.push(`Edited ${lastEdited}`);
  }

  // Create paper layers (3 layers behind the main card)
  const paperLayers = [1, 2, 3];

  return (
    <Box
      sx={{
        position: 'relative',
        width: '220px',
        aspectRatio: '8.5 / 11',
        isolation: 'isolate',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(urls.projectDetails.replace(':projectId', project._id))}
    >
      {/* Paper stack layers - behind the main card */}
      {paperLayers.map((layerIndex) => {
        const transform = getStackTransform(layerIndex);
        return (
          <Box
            key={`paper-layer-${layerIndex}`}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: 0,
              background: isDark
                ? `linear-gradient(135deg, rgba(30, 41, 59, ${0.85 - layerIndex * 0.1}) 0%, rgba(20, 30, 47, ${0.88 - layerIndex * 0.1}) 100%)`
                : `linear-gradient(135deg, rgba(255, 255, 255, ${0.9 - layerIndex * 0.08}) 0%, rgba(250, 250, 250, ${0.88 - layerIndex * 0.08}) 100%)`,
              border: isDark
                ? '1px solid rgba(255, 255, 255, 0.06)'
                : '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: isDark
                ? `0 1px 2px rgba(0, 0, 0, ${0.1 + layerIndex * 0.05})`
                : `0 1px 2px rgba(0, 0, 0, ${0.03 + layerIndex * 0.02})`,
              zIndex: -layerIndex,
              transform: isHovered
                ? 'translate(0, 0) rotate(0deg)'
                : `translate(${transform.translateX}px, ${transform.translateY}px) rotate(${transform.rotation}deg)`,
              transition: 'transform 400ms cubic-bezier(0.4, 0, 0.2, 1)',
              transformOrigin: 'center center',
            }}
          />
        );
      })}

      {/* Main card */}
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 0,
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(20, 30, 47, 0.98) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.95) 100%)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: isDark
            ? '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.03) inset'
            : '0 2px 8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02) inset',
          position: 'relative',
          zIndex: 0,
          transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: isDark ? 0.02 : 0.015,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&:hover': {
            boxShadow: isDark
              ? '0 12px 24px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.15) inset, 0 0 20px rgba(59, 130, 246, 0.08)'
              : '0 8px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.2) inset',
            borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.4)',
          },
        }}
      >
        <CardContent
          sx={{
            p: 5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
            '&:last-child': {
              pb: 5,
            },
          }}
        >
          {/* Shared badge - below status, top right */}
          {sharedRole && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                mt: 3.5,
                display: 'inline-flex',
                padding: '1px 6px',
                borderRadius: '3px',
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.35)',
                zIndex: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.65rem',
                  textTransform: 'capitalize',
                }}
              >
                Shared • {sharedRole}
              </Typography>
            </Box>
          )}

          {/* Status Pill - Top Right */}
          {status && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                display: 'inline-flex',
                padding: '1px 6px',
                borderRadius: '3px',
                backgroundColor: isDark ? `rgba(255, 255, 255, 0.08)` : `rgba(0, 0, 0, 0.05)`,
                border: isDark
                  ? `1px solid rgba(255, 255, 255, 0.12)`
                  : `1px solid rgba(0, 0, 0, 0.1)`,
                zIndex: 2,
                opacity: 0.7,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.muted,
                  fontSize: '0.65rem',
                  fontWeight: 400,
                  textTransform: 'capitalize',
                  opacity: 0.8,
                }}
              >
                {statusLabel}
              </Typography>
            </Box>
          )}

          {/* Hover Actions - Top Left */}
          {isHovered && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                left: 5,
                display: 'flex',
                gap: 0.5,
                zIndex: 2,
                animation: 'fadeIn 200ms ease',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateX(-4px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(urls.projectDetails.replace(':projectId', project._id));
                }}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  color: colors.text.secondary,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                    color: colors.text.primary,
                  },
                  transition: 'all 150ms ease',
                }}
              >
                <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Box>
          )}

          {/* Main Content */}
          <Box sx={{ width: '100%', px: 2 }}>
            {/* Project Title - Serif, Book-like */}
            <Typography
              sx={{
                color: isHovered ? colors.text.primary : colors.text.primary,
                fontSize: 'clamp(1.15rem, 1.6vw, 1.5rem)',
                fontWeight: 500,
                fontFamily: '"Georgia", "Times New Roman", serif',
                fontStyle: 'italic',
                mb: 3,
                lineHeight: 1.4,
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                letterSpacing: '-0.01em',
                opacity: isHovered ? 1 : 0.95,
                transition: 'opacity 200ms ease',
              }}
            >
              {projectTitle}
            </Typography>

            {/* Project Type */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                mb: 2,
              }}
            >
              <TypeIcon sx={{ fontSize: '0.9rem', color: colors.text.muted, opacity: 0.8 }} />
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.muted,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  opacity: 0.8,
                }}
              >
                {typeConfig.label}
              </Typography>
            </Box>

            {/* Metadata - Word Count and Last Edited */}
            {metadataParts.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {metadataParts.map((part) => (
                  <Typography
                    key={part}
                    variant="caption"
                    sx={{
                      color: colors.text.muted,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      opacity: 0.7,
                      lineHeight: 1.5,
                    }}
                  >
                    {part}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Word count goal progress */}
            {goalProgress !== null && (
              <Box sx={{ mt: 2, px: 1 }}>
                <Box
                  aria-label={`Goal progress: ${Math.round(goalProgress * 100)}%`}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${goalProgress * 100}%`,
                      height: '100%',
                      borderRadius: 2,
                      backgroundColor: goalProgress >= 1 ? '#22c55e' : colors.interactive.primary,
                      transition: 'width 400ms ease',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.muted, fontSize: '0.65rem', opacity: 0.7 }}
                >
                  {Math.round(goalProgress * 100)}% of {goalWordCount.toLocaleString()} words
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectCard;

ProjectCard.propTypes = {
  project: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    type: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    wordCount: PropTypes.number,
    goalWordCount: PropTypes.number,
    myRole: PropTypes.string,
    status: PropTypes.string,
    thinOwner: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      pseudonym: PropTypes.string,
    }),
    thinChapters: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinCoauthors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinEditors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinNotes: PropTypes.arrayOf(PropTypes.shape({})),
    isDeleted: PropTypes.bool,
  }).isRequired,
};
